import React, { useEffect } from 'react';
import {
    Form, Input, Button, Card, Space, Spin, Alert, Typography, Tabs, Row, Col,
} from 'antd';
import { useAuthStore } from '@/modules/auth/stores/auth-store';
import { BranchConfigDTO, useBranchConfig, useUpdateBranchConfig } from '../hooks/useSystemConfig';

const { Title } = Typography;

export const SystemConfigView: React.FC = () => {
    const branch = useAuthStore((s) => s.user?.branch ?? '');
    const { data, isLoading, error } = useBranchConfig(branch);
    const update = useUpdateBranchConfig();
    const [form] = Form.useForm<BranchConfigDTO>();

    useEffect(() => {
        if (data) form.setFieldsValue(data);
    }, [data, form]);

    const handleSave = async () => {
        const values = await form.validateFields();
        update.mutate({ ...values, branch });
    };

    if (error) return <Alert type="error" message="Failed to load configuration" />;

    return (
        <div style={{ padding: 24, maxWidth: 960 }}>
            <Title level={4}>System / Branch Configuration</Title>
            <Spin spinning={isLoading}>
                <Form form={form} layout="vertical">
                    <Tabs
                        items={[
                            {
                                key: 'basic',
                                label: 'Institution Info',
                                children: (
                                    <Card>
                                        <Row gutter={16}>
                                            <Col span={12}>
                                                <Form.Item label="Full Name" name="brFname">
                                                    <Input maxLength={254} />
                                                </Form.Item>
                                            </Col>
                                            <Col span={12}>
                                                <Form.Item label="Short Name" name="brName">
                                                    <Input maxLength={254} />
                                                </Form.Item>
                                            </Col>
                                            <Col span={12}>
                                                <Form.Item label="Registration Number" name="regNo">
                                                    <Input maxLength={254} />
                                                </Form.Item>
                                            </Col>
                                            <Col span={12}>
                                                <Form.Item label="Logo Path" name="logo">
                                                    <Input maxLength={254} />
                                                </Form.Item>
                                            </Col>
                                        </Row>
                                    </Card>
                                ),
                            },
                            {
                                key: 'contact',
                                label: 'Contact Details',
                                children: (
                                    <Card>
                                        <Row gutter={16}>
                                            <Col span={24}>
                                                <Form.Item label="Address" name="address">
                                                    <Input maxLength={254} />
                                                </Form.Item>
                                            </Col>
                                            <Col span={12}>
                                                <Form.Item label="Telephone" name="telephone">
                                                    <Input maxLength={254} />
                                                </Form.Item>
                                            </Col>
                                            <Col span={12}>
                                                <Form.Item label="Mobile" name="mobile">
                                                    <Input maxLength={254} />
                                                </Form.Item>
                                            </Col>
                                            <Col span={12}>
                                                <Form.Item label="Email" name="email">
                                                    <Input type="email" maxLength={254} />
                                                </Form.Item>
                                            </Col>
                                            <Col span={12}>
                                                <Form.Item label="Website" name="website">
                                                    <Input maxLength={254} />
                                                </Form.Item>
                                            </Col>
                                            <Col span={12}>
                                                <Form.Item label="Contact Person" name="contactPerson">
                                                    <Input maxLength={254} />
                                                </Form.Item>
                                            </Col>
                                        </Row>
                                    </Card>
                                ),
                            },
                            {
                                key: 'location',
                                label: 'Location & Branch Info',
                                children: (
                                    <Card>
                                        <Row gutter={16}>
                                            <Col span={12}>
                                                <Form.Item label="Region" name="region">
                                                    <Input maxLength={254} />
                                                </Form.Item>
                                            </Col>
                                            <Col span={12}>
                                                <Form.Item label="District" name="district">
                                                    <Input maxLength={254} />
                                                </Form.Item>
                                            </Col>
                                            <Col span={12}>
                                                <Form.Item label="Ward" name="ward">
                                                    <Input maxLength={254} />
                                                </Form.Item>
                                            </Col>
                                            <Col span={12}>
                                                <Form.Item label="Postal Address" name="postalAddress">
                                                    <Input maxLength={254} />
                                                </Form.Item>
                                            </Col>
                                            <Col span={12}>
                                                <Form.Item label="Manager Name" name="managerName">
                                                    <Input maxLength={254} />
                                                </Form.Item>
                                            </Col>
                                            <Col span={12}>
                                                <Form.Item label="Opening Hours" name="openingHours">
                                                    <Input maxLength={254} />
                                                </Form.Item>
                                            </Col>
                                            <Col span={12}>
                                                <Form.Item label="GPS Coordinates" name="gpsCoordinates">
                                                    <Input maxLength={254} placeholder="e.g. -6.7924, 39.2083" />
                                                </Form.Item>
                                            </Col>
                                            <Col span={24}>
                                                <Form.Item label="Services Offered" name="servicesOffered">
                                                    <Input.TextArea rows={3} />
                                                </Form.Item>
                                            </Col>
                                        </Row>
                                    </Card>
                                ),
                            },
                        ]}
                    />

                    <Space style={{ marginTop: 16 }}>
                        <Button
                            type="primary"
                            loading={update.isPending}
                            onClick={handleSave}
                        >
                            Save Configuration
                        </Button>
                    </Space>
                </Form>
            </Spin>
        </div>
    );
};
